import { CSSProperties, FC } from "react";

import { withActiveAv } from "components/av/AvSubsystemLoader";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Menu, MenuDivider, MenuItem } from "mds/components/Menu";
import { RoundedButton } from "mds/components/buttons/RoundedButton";
import { ChevronUpIcon, PresentPlayFilledIcon, PresentPlayIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectIsClassSessionInPerson, selectIsDesktopDevice } from "store/selectors";
import { selectCurrentBreakoutGroupId } from "store/selectors/breakouts";
import { useAv } from "utils/av";

const LazyScreenshareMenuOption = withActiveAv(() => import("components/av/ScreenshareMenuOption"));

export type PagePresentingMenuProps = {
  styles?: CSSProperties;
  onPractice?: () => void;
  onPresent?: () => void;
  isDisabled?: boolean;
  isPractice?: boolean;
};

export const PagePresentingMenu: FC<PagePresentingMenuProps> = ({
  styles,
  onPractice,
  onPresent,
  isDisabled,
  isPractice,
}) => {
  const isInPersonClassSession = useAppSelector(selectIsClassSessionInPerson);
  const { isInVirtualClass } = useAv();
  const currentBreakoutGroupId = useAppSelector(selectCurrentBreakoutGroupId);
  const isDesktopDevice = useAppSelector(selectIsDesktopDevice);
  const presentFunction = isPractice ? onPractice : onPresent;

  if (isInPersonClassSession) {
    return (
      <RoundedButton disabled={isDisabled} kind="tertiary" onClick={presentFunction}>
        <PresentPlayIcon />
        {isPractice ? t("page_presenting_menu.practice") : t("page_presenting_menu.present")}
      </RoundedButton>
    );
  }

  const MenuButton = (
    <RoundedButton disabled={isDisabled} kind="tertiary">
      <PresentPlayIcon />
      {t("page_presenting_menu.present")}
      <ChevronUpIcon />
    </RoundedButton>
  );

  // If the user is not in desktop and in a breakout group, there will be no available options
  if (currentBreakoutGroupId && !isDesktopDevice) return null;

  return (
    <Menu
      className="dark-menu min-w-[180px]"
      direction="top"
      menuButton={MenuButton}
      menuStyle={styles}
    >
      {!currentBreakoutGroupId && (
        <MenuItem eventName="Presentation Menu - Start" onClick={presentFunction}>
          <IconText
            iconStart={<PresentPlayFilledIcon className="text-green" />}
            text={
              isPractice
                ? t("page_presenting_menu.practice")
                : t("page_presenting_menu.start_presenting")
            }
          />
        </MenuItem>
      )}

      {isDesktopDevice && isInVirtualClass && !currentBreakoutGroupId && <MenuDivider />}

      {isDesktopDevice && <LazyScreenshareMenuOption isPractice={isPractice} />}
    </Menu>
  );
};
